/*
 * Browser-side report generator — mirrors packages/reporter/src/html.ts
 * Used by RecorderPanel to show a live report after replay in the Studio.
 */

export interface StudioRunResult {
  testName:    string;
  startedAt:   number;
  finishedAt:  number;
  steps: Array<{
    stepId:    string;
    stepType:  string;
    status:    'ok' | 'not_found' | 'error' | 'skipped';
    matchedBy?: string;
    durationMs: number;
    detail?:   string;
  }>;
}

function esc(s: string) {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

export function generateHTMLReport(runs: StudioRunResult[]): string {
  const totalSteps  = runs.reduce((n, r) => n + r.steps.length, 0);
  const passedSteps = runs.reduce((n, r) => n + r.steps.filter(s => s.status === 'ok').length, 0);
  const failedSteps = runs.reduce((n, r) => n + r.steps.filter(s => s.status !== 'ok' && s.status !== 'skipped').length, 0);
  const totalMs     = runs.reduce((n, r) => n + (r.finishedAt - r.startedAt), 0);

  const badge = (s: string) => {
    const map: Record<string, string> = {
      ok:        '<span style="color:#22c55e;font-weight:700">✓ OK</span>',
      not_found: '<span style="color:#ef4444;font-weight:700">✗ MISS</span>',
      error:     '<span style="color:#f59e0b;font-weight:700">⚠ ERR</span>',
      skipped:   '<span style="color:#71717a">— SKIP</span>',
    };
    return map[s] ?? `<span>${esc(s)}</span>`;
  };

  const testsHTML = runs.map(run => {
    const rows = run.steps.map((s, i) => `
      <tr style="border-bottom:1px solid #27272a;${s.status!=='ok'?'background:rgba(239,68,68,0.06)':''}">
        <td style="padding:5px 10px;color:#71717a">${i+1}</td>
        <td style="padding:5px 10px">${badge(s.status)}</td>
        <td style="padding:5px 10px;font-family:monospace;font-size:11px">${esc(s.stepType)}</td>
        <td style="padding:5px 10px;font-size:12px;color:#a1a1aa">${esc(s.detail ?? '')}</td>
        <td style="padding:5px 10px">${s.matchedBy ? `<span style="background:#3f3f46;border-radius:3px;padding:1px 5px;font-size:10px">${esc(s.matchedBy)}</span>` : '—'}</td>
        <td style="padding:5px 10px;color:#71717a;font-size:11px">${s.durationMs}ms</td>
      </tr>`).join('');

    const passed = run.steps.filter(s => s.status === 'ok').length;
    const runStatus = passed === run.steps.length ? 'passed' : 'failed';

    return `
    <details style="background:#18181b;border:1px solid ${runStatus==='passed'?'#27272a':'rgba(239,68,68,0.3)'};border-radius:8px;margin-bottom:10px;overflow:hidden" ${runStatus==='failed'?'open':''}>
      <summary style="padding:12px 16px;cursor:pointer;display:flex;gap:10px;align-items:center;list-style:none">
        <span style="font-weight:600;font-size:14px;flex:1;color:#e4e4e7">${esc(run.testName)}</span>
        <span style="color:${runStatus==='passed'?'#22c55e':'#ef4444'};font-weight:700">${runStatus==='passed'?'✓ PASSED':'✗ FAILED'}</span>
        <span style="color:#71717a;font-size:12px">${passed}/${run.steps.length} · ${((run.finishedAt-run.startedAt)/1000).toFixed(2)}s</span>
      </summary>
      <table style="width:100%;border-collapse:collapse;font-size:12px;border-top:1px solid #27272a">
        <thead><tr style="background:#111113"><th style="padding:6px 10px;text-align:left;color:#71717a;font-size:11px">#</th><th>Status</th><th>Type</th><th>Detail</th><th>Matched by</th><th>Time</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </details>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"/><title>Scriptwave Report</title>
<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:system-ui,sans-serif;background:#0f0f11;color:#e4e4e7;padding:20px}details summary::-webkit-details-marker{display:none}</style>
</head><body>
<h2 style="color:#c4b5fd;margin-bottom:4px;font-size:18px">Scriptwave Test Report</h2>
<p style="color:#71717a;font-size:12px;margin-bottom:20px">Run at ${new Date().toLocaleString()}</p>
<div style="display:flex;gap:10px;margin-bottom:20px">
  <div style="background:#18181b;border:1px solid #27272a;border-radius:8px;padding:12px 18px"><div style="font-size:22px;font-weight:700;color:#22c55e">${passedSteps}</div><div style="font-size:10px;color:#71717a">PASSED</div></div>
  <div style="background:#18181b;border:1px solid #27272a;border-radius:8px;padding:12px 18px"><div style="font-size:22px;font-weight:700;color:#ef4444">${failedSteps}</div><div style="font-size:10px;color:#71717a">FAILED</div></div>
  <div style="background:#18181b;border:1px solid #27272a;border-radius:8px;padding:12px 18px"><div style="font-size:22px;font-weight:700">${totalSteps}</div><div style="font-size:10px;color:#71717a">STEPS</div></div>
  <div style="background:#18181b;border:1px solid #27272a;border-radius:8px;padding:12px 18px"><div style="font-size:22px;font-weight:700">${(totalMs/1000).toFixed(1)}s</div><div style="font-size:10px;color:#71717a">DURATION</div></div>
</div>
${testsHTML}
</body></html>`;
}
